import { useEffect, useRef } from 'react';
import { usePlayer } from '@/context/PlayerContext';
import { Play, Pause, SkipBack, SkipForward, SpeakerHigh, SpeakerLow, SpeakerX } from '@phosphor-icons/react';
import { Slider } from '@/components/ui/slider';
import { motion, AnimatePresence } from 'framer-motion';

export const AudioPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    volume,
    audioRef,
    togglePlay,
    playNext,
    playPrevious,
    seekTo,
    setDuration,
    setCurrentTime,
    setVolume,
    setIsPlaying,
    getStreamUrl,
  } = usePlayer();

  const internalAudioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = internalAudioRef.current;
    }
  }, [audioRef]);

  useEffect(() => {
    const audio = internalAudioRef.current;
    if (!audio || !currentTrack) return;

    const streamUrl = getStreamUrl(currentTrack);
    if (!streamUrl) return;

    audio.src = streamUrl;
    audio.volume = volume;

    if (isPlaying) {
      audio.play().catch(console.error);
    }

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      playNext();
    };
    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [currentTrack, getStreamUrl, isPlaying, playNext, setCurrentTime, setDuration, setIsPlaying, volume]);

  useEffect(() => {
    const audio = internalAudioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const audio = internalAudioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const VolumeIcon = volume === 0 ? SpeakerX : volume < 0.5 ? SpeakerLow : SpeakerHigh;

  return (
    <>
      <audio ref={internalAudioRef} preload="metadata" />
      <AnimatePresence>
        {currentTrack && currentTrack.audio_path && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 z-50 glass"
          >
            <div className="max-w-screen-2xl mx-auto px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Track Info */}
                <div className="flex items-center gap-3 min-w-0 w-1/4">
                  <div className="w-12 h-12 rounded bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                    <div className="flex gap-0.5 items-end h-4">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-1 bg-[#CCFF00] rounded-full ${isPlaying ? 'soundwave-bar' : ''}`}
                          style={{ height: isPlaying ? undefined : '4px' }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-white truncate" data-testid="player-track-title">
                      {currentTrack.title}
                    </p>
                    <p className="text-sm text-[#A1A1AA] truncate">
                      {currentTrack.musician?.name || 'Unknown Artist'}
                    </p>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex-1 flex flex-col items-center gap-2">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={playPrevious}
                      className="text-[#A1A1AA] hover:text-white transition-colors"
                      data-testid="player-previous-btn"
                    >
                      <SkipBack size={20} weight="fill" />
                    </button>
                    <button
                      onClick={togglePlay}
                      className="w-10 h-10 rounded-full bg-[#CCFF00] text-black flex items-center justify-center hover:bg-[#E5FF00] transition-colors"
                      data-testid="player-play-btn"
                    >
                      {isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
                    </button>
                    <button
                      onClick={playNext}
                      className="text-[#A1A1AA] hover:text-white transition-colors"
                      data-testid="player-next-btn"
                    >
                      <SkipForward size={20} weight="fill" />
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-3 w-full max-w-xl">
                    <span className="text-xs text-[#A1A1AA] w-10 text-right">
                      {formatTime(currentTime)}
                    </span>
                    <Slider
                      value={[currentTime]}
                      max={duration || 100}
                      step={1}
                      onValueChange={([value]) => seekTo(value)}
                      className="flex-1"
                      data-testid="player-progress-slider"
                    />
                    <span className="text-xs text-[#A1A1AA] w-10">
                      {formatTime(duration)}
                    </span>
                  </div>
                </div>

                {/* Volume */}
                <div className="flex items-center gap-2 w-1/4 justify-end">
                  <button
                    onClick={() => setVolume(volume === 0 ? 0.8 : 0)}
                    className="text-[#A1A1AA] hover:text-white transition-colors"
                    data-testid="player-volume-btn"
                  >
                    <VolumeIcon size={20} weight="fill" />
                  </button>
                  <Slider
                    value={[volume * 100]}
                    max={100}
                    step={1}
                    onValueChange={([value]) => setVolume(value / 100)}
                    className="w-24"
                    data-testid="player-volume-slider"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
